import { colorsPalettes, rgba } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { OverlapTableStyled } from "@similarweb/ui-components";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { i18nFilter } from "app/filters/i18nFilter";
import { overlapTableDataFetcher } from "app/pages/website-analysis/audience-interests/single/AudienceInterestsOverlapTableService";
import { AudienceInterestsOverlapTableTooltip } from "app/pages/website-analysis/audience-interests/single/AudienceInterestsOverlapTableTooltip";
import { useEffect, useState } from "react";
import styled from "styled-components";
import type { IOverlapTableData, IAudienceMatrixAPI } from "./types";
import { CrossBrowsingOvertimeWrapper } from "app/pages/website-analysis/audience-interests/single/CrossBrowsingOvertime/CrossBrowsingOvertimeWrapper";
import { Header } from "app/pages/website-analysis/audience-interests/single/Header";

const Wrapper = styled.div`
    border-radius: 6px;
    background-color: ${colorsPalettes.carbon["0"]};
    box-shadow: 0 3px 6px 0 ${rgba(colorsPalettes.midnight[600], 0.08)};
    margin-bottom: 16px;
`;
const OverlapTableContainer = styled.div<{ withButton: boolean }>`
    padding: ${({ withButton }) => (withButton ? "32px 32px 8px 32px" : "32px")};
`;
const AudienceInterestsOverlapTableFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 8px 0 0;
`;
export const AudienceInterestsOverlapTable = (props) => {
    const i18n = i18nFilter();
    const [overlapTableData, setOverlapTableData] = useState<IOverlapTableData>({
        tableData: [],
        changeData: {},
    });
    const [rawData, setRawData] = useState<IAudienceMatrixAPI>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { chosenSites, country, duration, isWWW, onOverlapTableSelect, openCompareModal, webSource } = props;
    const { from, to, isWindow } = duration;
    const icons = {};
    const keysForApi = chosenSites
        .map((x) => {
            icons[x.name] = x.icon; // Hijacking the loop to populate icons object. Sorry.
            return x.name;
        })
        .join(",");
    const fetcherParams = {
        key: keysForApi,
        isWWW: isWWW !== "*",
        country,
        to,
        from,
        isWindow,
        webSource,
    };

    const transformResults = (results: IAudienceMatrixAPI): IOverlapTableData => {
        const changeData = {};
        const tableData = Object.keys(results).map((domain) => {
            const items = {};
            changeData[domain] = {};
            Object.keys(results).forEach((item) => {
                changeData[domain][item] = results[domain][item] ? results[domain][item].OverlapChange : null;
                items[item] = results[domain][item] ? results[domain][item].Overlap : NaN;
            });
            return { items, value: domain };
        });
        return { tableData, changeData };
    };
    useEffect(() => {
        setIsLoading(true);
        overlapTableDataFetcher({ params: fetcherParams }).then((results) => {
            setOverlapTableData(transformResults(results));
            setRawData(results);
            setIsLoading(false);
        });
    }, []);

    const onItemClick = (item) => {
        onOverlapTableSelect(item.name);
    };

    return (
        <>
            <Wrapper>
                <Header
                    tooltipKey={"analysis.audience.interests.overlap.table.tooltip"}
                    titleKey={"analysis.audience.interests.overlap.table.title"}
                />
                <OverlapTableContainer withButton={overlapTableData.tableData.length < 5}>
                    {overlapTableData.tableData.length > 0 ? (
                        <>
                            <OverlapTableStyled
                                data={overlapTableData.tableData}
                                onItemClick={onItemClick}
                                icons={icons}
                                horizontalTitle={i18n("analysis.audience.interests.overlap.table.horizontalTitle")}
                                verticalTitle={i18n("analysis.audience.interests.overlap.table.verticalTitle")}
                                getTooltipComponent={({ valueHorizontal, valueVertical, value }) =>
                                    () =>
                                        (
                                            <AudienceInterestsOverlapTableTooltip
                                                valueHorizontal={valueHorizontal}
                                                valueVertical={valueVertical}
                                                value={value}
                                                changeValue={
                                                    overlapTableData.changeData[valueVertical][valueHorizontal]
                                                }
                                            />
                                        )}
                            />
                            {overlapTableData.tableData.length < 5 && (
                                <AudienceInterestsOverlapTableFooter>
                                    <Button
                                        type="flat"
                                        onClick={openCompareModal}
                                    >
                                        {i18n("analysis.audience.interests.overlap.table.add.more.button")}
                                    </Button>
                                </AudienceInterestsOverlapTableFooter>
                            )}
                        </>
                    ) : (
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={"324px"}
                        />
                    )}
                </OverlapTableContainer>
            </Wrapper>
            <Wrapper>
                <CrossBrowsingOvertimeWrapper
                    rawData={rawData}
                    isLoading={isLoading}
                />
            </Wrapper>
        </>
    );
};
